/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, css } from '@compiled/react';

// ── Sub-type tile icon imports ──────────────────────────────────────────────
import areaChartSvg from './assets/area-chart.svg';
import barChartSvg from './assets/bar-chart.svg';
import flipCardsSvg from './assets/flip-cards.svg';
import flowchartSvg from './assets/flowchart.svg';
import funnelChartSvg from './assets/funnel-chart.svg';
import ganttChartSvg from './assets/gantt-chart.svg';
import geographicalMapSvg from './assets/geographical-map.svg';
import lineChartSvg from './assets/line-chart.svg';
import mindmapSvg from './assets/mindmap.svg';
import modernSvg from './assets/modern.svg';
import orgChartSvg from './assets/org-chart.svg';
import pieChartSvg from './assets/pie-chart.svg';
import quadrantSvg from './assets/quadrant.svg';
import sketchSvg from './assets/sketch.svg';
import timelineSvg from './assets/timeline.svg';
import treemapSvg from './assets/treemap.svg';
import type { KnownSubType } from './types';

// ── Icon components ─────────────────────────────────────────────────────────

const styles = css({ width: '100%', height: '100%', objectFit: 'contain' });
const SubTypeIcon = ({ src, alt }: { src: string; alt: string }) => (
	<img src={src} alt={alt} css={styles} aria-hidden="true" />
);

// Charts
export const LineChartIcon = () => <SubTypeIcon src={lineChartSvg} alt="Line chart" />;
export const AreaChartIcon = () => <SubTypeIcon src={areaChartSvg} alt="Area chart" />;
export const BarChartIcon = () => <SubTypeIcon src={barChartSvg} alt="Bar chart" />;
export const FunnelChartIcon = () => <SubTypeIcon src={funnelChartSvg} alt="Funnel chart" />;
export const PieChartIcon = () => <SubTypeIcon src={pieChartSvg} alt="Pie chart" />;
export const GanttChartIcon = () => <SubTypeIcon src={ganttChartSvg} alt="Gantt chart" />;

// Diagrams
export const MindMapIcon = () => <SubTypeIcon src={mindmapSvg} alt="Mind map" />;
export const FlowChartIcon = () => <SubTypeIcon src={flowchartSvg} alt="Flowchart" />;

// Infographics
export const ModernStyleIcon = () => <SubTypeIcon src={modernSvg} alt="Modern style" />;
export const SketchStyleIcon = () => <SubTypeIcon src={sketchSvg} alt="Sketch style" />;

// Visual aids
export const FlipCardIcon = () => <SubTypeIcon src={flipCardsSvg} alt="Flip cards" />;
export const QuadrantIcon = () => <SubTypeIcon src={quadrantSvg} alt="Quadrant" />;
export const TreeMapIcon = () => <SubTypeIcon src={treemapSvg} alt="Tree map" />;
export const TimelineIcon = () => <SubTypeIcon src={timelineSvg} alt="Timeline" />;
export const GeographicalMapIcon = () => (
	<SubTypeIcon src={geographicalMapSvg} alt="Geographical map" />
);
export const OrgChartIcon = () => <SubTypeIcon src={orgChartSvg} alt="Org chart" />;

/**
 * Maps each sub-type value to its icon component.
 * Used by the sub-type grid to render the correct icon for each tile.
 *
 * Typed as `Record<KnownSubType, …>` so a missing entry is a compile-time error.
 */
export const SUB_TYPE_ICON_MAP: Record<KnownSubType, () => JSX.Element> = {
	// Charts
	'line-chart': LineChartIcon,
	'area-chart': AreaChartIcon,
	'bar-chart': BarChartIcon,
	'funnel-chart': FunnelChartIcon,
	'pie-chart': PieChartIcon,
	'gantt-chart': GanttChartIcon,
	// Diagrams
	mindmap: MindMapIcon,
	flowchart: FlowChartIcon,
	// Infographics
	'corporate-modern': ModernStyleIcon,
	'corporate-doodle': SketchStyleIcon,
	// Visual aids
	'flip-cards': FlipCardIcon,
	quadrant: QuadrantIcon,
	'tree-map': TreeMapIcon,
	'horizontal-timeline': TimelineIcon,
	map: GeographicalMapIcon,
	'org-chart': OrgChartIcon,
};
